<script>
    import { onMount } from "svelte";

    import { isMobile, user } from "../../stores";

    import {
        SvelteTable,
        SearchInput,
        Button,
        PageLoading,
        TableElementEditableStatus,
    } from "../../Components/Common/index.svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import {
        decodeSearchParams,
        encodeSearchParams,
        getFormatDate,
        getMFAType,
        getSVGClassNameFromClick,
        getText,
        getUserStatus,
        goToURL,
        replaceURL,
        sendResetPwdEmail,
        showError,
    } from "../../Utils/utilFunctions.svelte";

    import { deleteIcon } from "../../Utils/utilConstants.svelte";

    import TableFooter from "../../Components/Layout/Table/TableFooter.svelte";
    import NotificationModal from "../../Components/Common/NotificationModal.svelte";

    export let params = {};

    let isGettingAdmin = false,
        isDeleteAdminModalOpen = false,
        selectedAdminId,
        returnAdminArr = [],
        selectedAdmins = [],
        selectedAdminsIdArray = [],
        filters = {},
        checkboxString = "",
        page = 0,
        pageSize = 25,
        count;

    const columns = [
        {
            key: "selected?",
            title: "",
            class: "",
            headerClass: "toggleSelectAllHeader text-center ml-3 rounded-tl-xl",
            value: (v) => v.selected,
            renderValue: (v) =>
                `<input type="checkbox" class="checkbox checkbox-primary checkbox-xs ml-3 text-white cursor-pointer" name="selectCheckBox" id="selectCheckBoxId${v.id}">`,
            csvExportHidden: true,
        },
        {
            // sortable
            key: "id",
            title: "ID",
            value: (v) => getText(v.id),
            headerClass:
                "text-left pl-3 text-[14px] sortable-header-decoration",
            class: "text-left pl-3 text-[14px] cursor-pointer",
        },
        {
            // sortable
            key: "name",
            title: "Full name",
            value: (v) => getText(v.fullName),
            headerClass:
                "text-left pl-3 text-[14px] sortable-header-decoration",
            class: "text-left pl-3 text-[14px] cursor-pointer",
        },
        {
            key: "email",
            title: "Email",
            value: (v) => getText(v.email),
            headerClass: "text-left pl-3 text-[14px] cursor-default",
            class: "text-left pl-3 text-[14px] cursor-pointer",
        },
        {
            key: "phone",
            title: "Phone",
            value: (v) => getText(v.phone),
            headerClass: "text-left pl-3 text-[14px] cursor-default",
            class: "text-left pl-3 text-[14px] cursor-pointer",
        },
        {
            key: "mfa",
            title: "MFA",
            value: (v) => getMFAType(v.mfa),
            headerClass: "text-left pl-3 text-[14px] cursor-default",
            class: "text-left pl-3 text-[14px] cursor-pointer",
        },
        {
            key: "createdDate",
            title: "Created at",
            renderValue: (v) => getFormatDate(v.createdDate),
            headerClass: "text-left pl-3 text-[14px] cursor-default",
            class: "text-left pl-3 text-[14px] cursor-pointer",
        },
        {
            key: "loggedInDate",
            title: "Latest login",
            renderValue: (v) => getFormatDate(v.loggedInDate),
            headerClass: "text-left pl-3 text-[14px] cursor-default",
            class: "text-left pl-3 text-[14px] cursor-pointer",
        },
        {
            key: "loginAttempt",
            title: "Login attempts",
            value: (v) => (v.loginAttempts ? getText(v.loginAttempts) : "0"),
            headerClass: "text-left pl-3 text-[14px] cursor-default",
            class: "text-left pl-3 text-[14px] cursor-pointer",
        },
        {
            key: "status",
            title: "Status",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3",
            renderComponent: {
                component: TableElementEditableStatus,
                props: {
                    type: "admin",
                    onFinish: () =>
                        loadAdmins(
                            page * pageSize,
                            pageSize,
                            _getSearchParams(),
                        ),
                },
            },
            csvContent: (v) => `${getUserStatus(v.status)}`,
        },
        {
            key: "buttons",
            title: "Actions",
            renderValue: (v) => renderTableButtons(),
            headerClass: "text-left px-3 text-[14px] rounded-tr-xl px-3 w-[2%]",
            class: "text-left px-3 cursor-pointer text-[14px]",
            csvExportHidden: true,
        },
    ];

    const sortableHeaderKeys = ["id", "name"];

    $: _getCheckboxString(selectedAdminsIdArray);
    $: if (selectedAdmins.length >= 0 && returnAdminArr.length > 0) {
        selectedAdminsIdArray = selectedAdmins.map((admin) => admin.id);
        _updateHeaderIcon();
    }

    onMount(async () => {
        if (params.query) updateSearchParamsByQuery(params.query);

        if (!$user.email) await loadUserDetails();

        await loadAdmins(page * pageSize, pageSize, _getSearchParams());

        if (params.query) updateCheckboxByQuery(params.query);
    });

    const loadUserDetails = async () =>
        await apiMethods.getMethod.getUserById($user.id).then((res) => {
            if (!res || res.error)
                showError(
                    res.error ??
                        "Failed to get user email, please refresh the page and try again and contact support if the issue persists.",
                );
            else $user.email = res.email;
        });

    const updateSearchParamsByQuery = (query) => {
        const arr = decodeSearchParams(query).split("&");

        if (arr.length) {
            for (let i = 0; i < arr.length; i++) {
                if (arr[i].includes("page="))
                    page = Number(arr[i].split("=")[1]);

                if (arr[i].includes("size="))
                    pageSize = Number(arr[i].split("=")[1]);

                if (arr[i].includes("sort="))
                    filters.sort = arr[i].split("=")[1];

                if (arr[i].includes("search="))
                    filters.adminName = arr[i].split("=")[1].toString();
            }

            updateURL();
        }
    };

    const updateCheckboxByQuery = (query) => {
        const arr = query.split("&");
        if (arr.length) {
            for (let i = 0; i < arr.length; i++) {
                if (arr[i].includes("check=")) {
                    const checkArr = arr[i].split("=")[1].split(",");
                    checkArr.map((ca) => {
                        if (
                            !selectedAdmins.some(
                                (admin) => admin.id == Number(ca),
                            )
                        ) {
                            let newAdminItem = { id: Number(ca) };
                            selectedAdmins = [...selectedAdmins, newAdminItem];
                            const checkbox = document.getElementById(
                                `selectCheckBoxId${ca}`,
                            );
                            if (checkbox) checkbox.checked = true;
                        }
                    });
                }
            }
        }
    };

    const loadAdmins = (start, limit, params) => {
        isGettingAdmin = true;

        apiMethods.getMethod
            .getAdmin(start, limit, params)
            .then((res) => _handleAdmins(res))
            .then(() => _updateHeaderSortIcon());
    };

    const handleFilterSelect = () => {
        page = 0;
        selectedAdmins = [];

        updateURL();

        loadAdmins(0, pageSize, _getSearchParams());
    };

    const onPageChange = (e) => {
        page = Number(e.detail.page);

        updateURL();

        loadAdmins(
            Number(e.detail.firstDataIndex),
            pageSize,
            _getSearchParams(),
        );
    };

    const onPageSizeChange = (newPageSize) => {
        pageSize = newPageSize;
        page = 0;

        updateURL();

        loadAdmins(0, newPageSize, _getSearchParams());
    };

    const handleClickCell = (event, rowData, key) => {
        const classList = getSVGClassNameFromClick(event);

        if (key == "selected?") _addSelectedAdmin(rowData);
        else if (key === "buttons") {
            if (classList?.includes("deleteIcon")) {
                selectedAdminId = rowData.id;
                isDeleteAdminModalOpen = true;
            }
            if (classList?.includes("resetPasswordIcon") && rowData.email)
                sendResetPwdEmail(rowData.email);
        } else if (key !== "buttons" && key !== "status")
            goToURL(`/Admin/view/${rowData.id}`);
    };

    const handleClickCol = (event, colData, key) => {
        if (key == "selected?") _updateCheckBox();
        else if (sortableHeaderKeys.includes(key)) {
            if (filters.sort == key) filters.sort = `${key}desc`;
            else filters.sort = key;
            updateURL();
            loadAdmins(0, pageSize, _getSearchParams());
        }
    };

    const createAdmin = () => {
        goToURL("/Admin/create/new");
    };

    const deleteAdmin = () => {
        apiMethods.deleteMethod
            .deleteAdmin(selectedAdminId)
            .then((res) => {
                if (res.id) selectedAdminId = undefined;
            })
            .then(() => (isDeleteAdminModalOpen = false))
            .then(() =>
                loadAdmins(page * pageSize, pageSize, _getSearchParams()),
            );
    };

    const renderTableButtons = () => {
        return `<div class="flex flex-1 items-center justify-start gap-x-2">
                    <div class="resetPasswordIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center cursor-pointer w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Send Reset Password Email">
                        <svg fill="#6F726C" xmlns="http://www.w3.org/2000/svg" class="resetPasswordIcon hover:text-white" width="16px" height="16px" viewBox="0 0 52 52" enable-background="new 0 0 52 52" xml:space="preserve" stroke="#6F726C"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <g> <path d="M42,23H10c-2.2,0-4,1.8-4,4v19c0,2.2,1.8,4,4,4h32c2.2,0,4-1.8,4-4V27C46,24.8,44.2,23,42,23z M31,44.5 c-1.5,1-3.2,1.5-5,1.5c-0.6,0-1.2-0.1-1.8-0.2c-2.4-0.5-4.4-1.8-5.7-3.8l3.3-2.2c0.7,1.1,1.9,1.9,3.2,2.1c1.3,0.3,2.6,0,3.8-0.8 c2.3-1.5,2.9-4.7,1.4-6.9c-0.7-1.1-1.9-1.9-3.2-2.1c-1.3-0.3-2.6,0-3.8,0.8c-0.3,0.2-0.5,0.4-0.7,0.6L26,37h-9v-9l2.6,2.6 c0.4-0.4,0.9-0.8,1.3-1.1c2-1.3,4.4-1.8,6.8-1.4c2.4,0.5,4.4,1.8,5.7,3.8C36.2,36.1,35.1,41.7,31,44.5z"></path> <path d="M10,18.1v0.4C10,18.4,10,18.3,10,18.1C10,18.1,10,18.1,10,18.1z"></path> <path d="M11,19h4c0.6,0,1-0.3,1-0.9V18c0-5.7,4.9-10.4,10.7-10C32,8.4,36,13,36,18.4v-0.3c0,0.6,0.4,0.9,1,0.9h4 c0.6,0,1-0.3,1-0.9V18c0-9.1-7.6-16.4-16.8-16c-8.5,0.4-15,7.6-15.2,16.1C10.1,18.6,10.5,19,11,19z"></path> </g> </g></svg>
                    </div>
                    <div class="deleteIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Delete Admin">
                        <i class="deleteIcon ${deleteIcon}"></i>
                    </div>
                </div>`;
    };

    const generateSelectAll = () => {
        const selectAllHeader = document.getElementsByClassName(
            "toggleSelectAllHeader",
        )[0];
        const tag = document.createElement("input");
        tag.type = "checkbox";
        tag.className =
            "toggleSelectAll text-center hover:cursor-pointer checkbox checkbox-primary checkbox-xs bg-white ml-3 mt-2";
        tag.id = "toggleSelectAll";
        tag.onclick = () => {
            toggleSelectAll();
        };
        if (selectAllHeader) selectAllHeader.appendChild(tag);
    };

    const toggleSelectAll = () => {
        const selectAllCheckBox = document.getElementById("toggleSelectAll");
        if (selectAllCheckBox.className.includes("selectedAll")) {
            selectedAdmins = [];
            _updateCheckBox();
        } else {
            returnAdminArr.forEach((admin) => {
                if (!selectedAdminsIdArray.includes(admin.id)) {
                    _addSelectedAdmin(admin);
                }
            });
        }
    };

    const updateURL = () => {
        if (checkboxString)
            replaceURL(
                `/Admin/page=${page}&size=${pageSize}&check=${checkboxString}${_getParamsString()}`,
            );
        else
            replaceURL(
                `/Admin/page=${page}&size=${pageSize}${_getParamsString()}`,
            );
    };

    // HELPER FUNCTIONS
    const _handleAdmins = (res) => {
        isGettingAdmin = false;
        if (res.error) showError(res.error);
        else {
            returnAdminArr = res.results;
            count = res.total;
        }
        setTimeout(() => {
            generateSelectAll();
            _updateCheckBox();
        }, 0);
    };

    const _closeDeletingModal = () => {
        isDeleteAdminModalOpen = false;
        selectedAdminId = undefined;
    };

    const _addSelectedAdmin = (data) => {
        if (selectedAdmins.some((item) => item.id === data.id)) {
            selectedAdmins = selectedAdmins.filter(
                (item) => item.id !== data.id,
            );
        } else {
            selectedAdmins = [...selectedAdmins, data];
            const checkbox = document.getElementById(
                `selectCheckBoxId${data.id}`,
            );
            if (checkbox) checkbox.checked = true;
        }
    };

    const _updateHeaderIcon = () => {
        const headerIcon = document.getElementById("toggleSelectAll");
        if (!headerIcon) return;
        if (selectedAdmins.length === 0) {
            headerIcon.className =
                "toggleSelectAll text-center hover:cursor-pointer checkbox checkbox-primary checkbox-xs bg-white mt-2 ml-3 selectedNone";
            headerIcon.checked = false;
            headerIcon.style = "";
        } else if (
            returnAdminArr.every((admin) =>
                selectedAdminsIdArray.includes(admin.id),
            )
        ) {
            headerIcon.style.cssText = "";
            headerIcon.className =
                "toggleSelectAll text-center hover:cursor-pointer checkbox checkbox-primary checkbox-xs bg-white mt-2 ml-3 selectedAll";
            headerIcon.checked = true;
        } else {
            headerIcon.className =
                "toggleSelectAll text-center hover:cursor-pointer checkbox checkbox-primary checkbox-xs bg-white mt-2 ml-3 appearance-none selectedPartial";
            headerIcon.checked = false;
            headerIcon.style.cssText =
                "border: solid #270053; border-width: 4px; padding: 4pxl border-radius: 5px; background-color: white; cursor: pointer; margin: 0.5rem 0 0 0.75rem";
        }
    };

    const _updateCheckBox = () => {
        setTimeout(() => {
            returnAdminArr.forEach((admin) => {
                const checkbox = document.getElementById(
                    `selectCheckBoxId${admin.id}`,
                );
                if (checkbox) {
                    if (selectedAdminsIdArray.includes(admin.id)) {
                        document.getElementById(
                            `selectCheckBoxId${admin.id}`,
                        ).checked = true;
                    } else
                        document.getElementById(
                            `selectCheckBoxId${admin.id}`,
                        ).checked = false;
                }
            });
            _updateHeaderIcon();
        }, 10);
    };

    const _getSearchParams = () => {
        let params = {};
        if (filters.sort) params.sort = filters.sort;

        if (filters.adminName) params.search = filters.adminName;

        return params;
    };

    const _getParamsString = () => {
        let str = "";
        const params = _getSearchParams();

        if (params) {
            if (params.sort) str += `&sort=${params.sort}`;

            if (params.search) str += `&search=${params.search}`;
        }

        return encodeSearchParams(str);
    };

    const _getCheckboxString = (arr) => {
        if (arr.length) checkboxString = arr.toString();
        else checkboxString = "";

        updateURL();
    };

    const _updateHeaderSortIcon = () => {
        let thRow = document.getElementsByTagName("th");
        const params = _getSearchParams();

        if (params) {
            setTimeout(() => {
                const upSpan = document.createElement("span");
                const downSpan = document.createElement("span");
                upSpan.innerHTML = `<i class="fas fa-sort-alpha-down" />`;
                upSpan.style = "font-size: 10px;";
                downSpan.innerHTML = `<i class="fas fa-sort-alpha-up-alt" />`;
                downSpan.style = "font-size: 10px;";

                Array.from(thRow).forEach((row) => {
                    if (
                        (params.sort == "name" &&
                            row.childNodes[0].data == "Full name") ||
                        (params.sort == "id" && row.childNodes[0].data == "ID")
                    )
                        row.appendChild(upSpan);
                    else if (
                        (params.sort == "namedesc" &&
                            row.childNodes[0].data == "Full name") ||
                        (params.sort == "iddesc" &&
                            row.childNodes[0].data == "ID")
                    )
                        row.appendChild(downSpan);
                });
            }, 0);
        }
    };
</script>

<div class="flex flex-col flex-1 gap-y-4">
    <div
        class="flex flex-0 {$isMobile
            ? 'flex-col gap-y-4  items-start'
            : 'justify-between items-end'}"
    >
        <div class="flex gap-4 flex-wrap">
            <SearchInput
                bind:value={filters.adminName}
                title="Search"
                inputFunc={handleFilterSelect}
                isLong
            />
        </div>
        <Button clickFunc={createAdmin}>Create Admin</Button>
    </div>

    <div class={$isMobile ? "h-auto w-minus48" : "relative flex-1"}>
        {#if isGettingAdmin}
            <PageLoading />
        {:else}
            <div
                class={$isMobile
                    ? "overflow-x-auto overflow-y-visible"
                    : "overflow-y-auto absolute inset-0"}
            >
                <SvelteTable
                    {columns}
                    rows={returnAdminArr}
                    clickCellFunc={({ detail: { event, row, key } }) => {
                        handleClickCell(event, row, key);
                    }}
                    clickColFunc={({ detail: { event, row, key } }) => {
                        handleClickCol(event, row, key);
                    }}
                />
            </div>
        {/if}
    </div>

    <TableFooter
        {page}
        {pageSize}
        {count}
        showSelected
        selectedCount={selectedAdmins.length}
        tableData={returnAdminArr}
        {onPageChange}
        {onPageSizeChange}
        csvConfig={{
            columns,
            data: returnAdminArr,
            fileName: "Admins",
            allDataApi: async () =>
                await apiMethods.getMethod.getAdmin(
                    0,
                    999999,
                    _getSearchParams(),
                ),
        }}
    />
</div>

{#if isDeleteAdminModalOpen}
    <NotificationModal
        title="Warning"
        isModalOpen={isDeleteAdminModalOpen}
        isReverseBtns
        cancelText="Delete"
        submitText="Cancel"
        confirmButtonClass="btn-accent"
        closeModal={deleteAdmin}
        confirmAction={_closeDeletingModal}
        closeModalFunction={_closeDeletingModal}
    >
        Are you sure you want to delete this account?
    </NotificationModal>
{/if}
